import { isAxiosError } from "axios"
import { vitalFill } from "@/helpers/vitals"
import {
  IBattleAbilityButton,
  IBattleActionChoice,
  IBattleActionsModel,
  IBattleEndStep,
  IBattleFieldCellView,
  IBattleSide,
  IBattleTextPart,
  IBattleScreenStatus,
  IRoundLogLine
} from "@/hooks/useBattleScreen/types"
import {
  ABILITY_CAST_OUTCOME,
  ABILITY_EFFECT_KIND,
  BATTLE_ACTION_TYPE,
  BATTLE_COLUMN,
  BATTLE_FIGHTER_KIND,
  BATTLE_MOVE_DIRECTION,
  BATTLE_ROUND_RESOLVER,
  BATTLE_ROW,
  BATTLE_STATE,
  BATTLE_TEAM,
  COMBAT_EVENT_TYPE,
  IBattleActionRequest,
  IBattleFighter,
  IBattleLegalActions,
  IBattleRound,
  IBattleView,
  ICombatEvent,
  MOVE_STATUS
} from "@/store/apis/battleAPI/battle.types"
import { planBattleEnd } from "@/store/apis/battleAPI/battleEnd"
import { IVitalView } from "@/types/vitals.types"

const line = (
  key: string,
  params: Record<string, number> = {},
  abilityId: string | null = null
): IRoundLogLine => ({ key, params, abilityId })

const failedCastKeys: Record<ABILITY_CAST_OUTCOME, string> = {
  [ABILITY_CAST_OUTCOME.SUCCESS]: "log.youCast",
  [ABILITY_CAST_OUTCOME.NO_ENERGY]: "log.castNoEnergy",
  [ABILITY_CAST_OUTCOME.ON_COOLDOWN]: "log.castOnCooldown",
  [ABILITY_CAST_OUTCOME.NO_VALID_TARGET]: "log.castNoTarget"
}

const failedMoveKeys: Record<
  Exclude<MOVE_STATUS, MOVE_STATUS.SUCCESS>,
  string
> = {
  [MOVE_STATUS.CELL_OCCUPIED]: "log.moveBlocked",
  [MOVE_STATUS.COLLISION_LOST]: "log.moveCollision",
  [MOVE_STATUS.OUT_OF_BOUNDS]: "log.moveInvalid",
  [MOVE_STATUS.INVALID_TEAM_BOUNDARY]: "log.moveInvalid"
}

const linesForEvent = (
  event: ICombatEvent,
  heroFighterId: string
): IRoundLogLine[] => {
  const isHero = (id: string) => id === heroFighterId
  const pick = (id: string, you: string, mob: string) =>
    isHero(id) ? you : mob

  switch (event.type) {
    case COMBAT_EVENT_TYPE.STRIKE: {
      const { hit, fizzled, crit, damage } = event.result
      const attacker = event.attackerId
      if (!hit || fizzled) {
        return [line(pick(attacker, "log.youMiss", "log.mobMiss"))]
      }
      return [
        line(
          crit
            ? pick(attacker, "log.youCrit", "log.mobCrit")
            : pick(attacker, "log.youHit", "log.mobHit"),
          { damage }
        )
      ]
    }
    case COMBAT_EVENT_TYPE.ABILITY_CAST: {
      const key =
        event.outcome === ABILITY_CAST_OUTCOME.SUCCESS
          ? pick(event.casterId, "log.youCast", "log.mobCast")
          : failedCastKeys[event.outcome]
      return [line(key, {}, event.abilityId)]
    }
    case COMBAT_EVENT_TYPE.MOVE: {
      if (event.status === MOVE_STATUS.SUCCESS) {
        return [line(pick(event.characterId, "log.youMove", "log.mobMove"))]
      }
      return isHero(event.characterId)
        ? [line(failedMoveKeys[event.status])]
        : []
    }
    case COMBAT_EVENT_TYPE.ABILITY_HIT: {
      const key =
        event.kind === ABILITY_EFFECT_KIND.HEAL
          ? pick(event.targetId, "log.abilityHealYou", "log.abilityHealMob")
          : pick(event.targetId, "log.abilityHitYou", "log.abilityHitMob")
      return [line(key, { amount: event.amount }, event.abilityId)]
    }
    case COMBAT_EVENT_TYPE.EFFECT_TICK: {
      const lines: IRoundLogLine[] = []
      if (event.damage > 0) {
        lines.push(
          line(
            pick(event.characterId, "log.tickDamageYou", "log.tickDamageMob"),
            { damage: event.damage }
          )
        )
      }
      if (event.heal > 0) {
        lines.push(
          line(pick(event.characterId, "log.tickHealYou", "log.tickHealMob"), {
            heal: event.heal
          })
        )
      }
      return lines
    }
    case COMBAT_EVENT_TYPE.ABILITY_EFFECT_APPLIED:
      return [
        line(
          pick(event.targetId, "log.effectOnYou", "log.effectOnMob"),
          {},
          event.abilityId
        )
      ]
    case COMBAT_EVENT_TYPE.ELIMINATION:
      return [line(pick(event.characterId, "log.youFell", "log.mobFell"))]
    default:
      return []
  }
}

export const formatRoundLog = (
  round: IBattleRound | null,
  heroFighterId: string
): IRoundLogLine[] => {
  if (!round) {
    return []
  }
  const timeout =
    round.resolvedBy === BATTLE_ROUND_RESOLVER.TIMEOUT
      ? [line("log.timeout")]
      : []
  return [
    ...timeout,
    ...round.events.flatMap((event) => linesForEvent(event, heroFighterId))
  ]
}

export const heroOf = (view: IBattleView): IBattleFighter | undefined =>
  view.fighters.find((fighter) => fighter.fighterId === view.heroFighterId)

export const mobOf = (view: IBattleView): IBattleFighter | undefined =>
  view.fighters.find((fighter) => fighter.kind === BATTLE_FIGHTER_KIND.MOB)

const abilityDetails = (
  energyCost: number,
  cooldownRounds: number,
  cooldownLeft: number | null
): IBattleTextPart[] => {
  if (cooldownLeft !== null) {
    return [{ key: "screen.cooldown", params: { rounds: cooldownLeft } }]
  }
  return [
    ...(energyCost > 0
      ? [{ key: "screen.energyCost", params: { cost: energyCost } }]
      : []),
    ...(cooldownRounds > 0
      ? [{ key: "screen.cooldownBase", params: { rounds: cooldownRounds } }]
      : [])
  ]
}

interface IDeriveBattleActionsInput {
  view: IBattleView
  legal: IBattleLegalActions | undefined
  busy: boolean
}

export const canHeroAct = ({
  view,
  legal,
  busy
}: IDeriveBattleActionsInput): boolean =>
  view.state === BATTLE_STATE.OPEN && !view.auto && !busy && legal !== undefined

export const deriveBattleActions = ({
  view,
  legal,
  busy
}: IDeriveBattleActionsInput): IBattleActionsModel => {
  const isOpen = view.state === BATTLE_STATE.OPEN
  const canAct = canHeroAct({ view, legal, busy })
  const mobId = mobOf(view)?.fighterId
  const hero = heroOf(view)
  const cooldowns = hero?.cooldowns ?? {}

  const abilities: IBattleAbilityButton[] = (hero?.abilities ?? []).map(
    ({ id, energyCost, cooldownRounds }) => {
      const left = cooldowns[id]
      const cooldownLeft = left > 0 ? left : null
      const isLegal =
        legal !== undefined &&
        (legal.attackAbilityIds.includes(id) ||
          legal.standaloneAbilityIds.includes(id))
      return {
        abilityId: id,
        enabled: canAct && cooldownLeft === null && isLegal,
        energyCost,
        cooldownRounds,
        cooldownLeft,
        details: abilityDetails(energyCost, cooldownRounds, cooldownLeft)
      }
    }
  )

  return {
    attack: {
      enabled:
        canAct &&
        mobId !== undefined &&
        (legal?.attackTargetIds.includes(mobId) ?? false)
    },
    abilities,
    autoOn: view.auto,
    autoEnabled: isOpen && !busy,
    fleeEnabled: isOpen && !busy
  }
}

export const actionRequestFor = (
  choice: IBattleActionChoice,
  view: IBattleView,
  legal: IBattleLegalActions
): IBattleActionRequest | null => {
  if (choice.kind === "move") {
    return { type: BATTLE_ACTION_TYPE.MOVE, direction: choice.direction }
  }
  const mobId = mobOf(view)?.fighterId
  if (choice.kind === "attack") {
    return mobId === undefined
      ? null
      : { type: BATTLE_ACTION_TYPE.ATTACK, targetId: mobId }
  }
  if (legal.attackAbilityIds.includes(choice.abilityId)) {
    return mobId === undefined
      ? null
      : {
          type: BATTLE_ACTION_TYPE.ATTACK,
          targetId: mobId,
          abilityId: choice.abilityId
        }
  }
  if (legal.standaloneAbilityIds.includes(choice.abilityId)) {
    return {
      type: BATTLE_ACTION_TYPE.ABILITY_ONLY,
      abilityId: choice.abilityId,
      abilityTargetId: view.heroFighterId
    }
  }
  return null
}

export const countdownSeconds = (
  deadline: string | null,
  nowMs: number
): number => {
  if (deadline === null) {
    return 0
  }
  return Math.max(0, Math.ceil((Date.parse(deadline) - nowMs) / 1000))
}

export const REREAD_AUTO_POLL_MS = 5000
export const REREAD_DEADLINE_GRACE_MS = 2000

/** Delay before the watchdog re-reads an open battle over REST; null when it should not. */
export const rereadDelayMs = (
  view: IBattleView | undefined,
  nowMs: number
): number | null => {
  if (!view || view.state !== BATTLE_STATE.OPEN) {
    return null
  }
  if (view.auto) {
    return REREAD_AUTO_POLL_MS
  }
  if (view.roundDeadline === null) {
    return null
  }
  return (
    Math.max(0, Date.parse(view.roundDeadline) - nowMs) +
    REREAD_DEADLINE_GRACE_MS
  )
}

export const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}:${String(rest).padStart(2, "0")}`
}

export const fighterVitals = (
  fighter: IBattleFighter,
  options: { withEnergy: boolean }
): IVitalView[] => {
  const view = (code: string, current: number, max: number): IVitalView => ({
    code,
    current,
    max,
    fill: vitalFill(current, max)
  })
  const vitals = [view("hp", fighter.hp, fighter.maxHp)]
  if (fighter.maxMana !== null) {
    vitals.push(view("mana", fighter.mana ?? 0, fighter.maxMana))
  }
  if (options.withEnergy) {
    vitals.push(view("energy", fighter.energy, fighter.maxEnergy))
  }
  return vitals
}

interface IScreenStatusInput {
  view: IBattleView | undefined
  isError: boolean
  errorStatus: number | null
}

export const screenStatusOf = ({
  view,
  isError,
  errorStatus
}: IScreenStatusInput): IBattleScreenStatus | "gone" => {
  if (view) {
    return "ready"
  }
  if (isError) {
    return errorStatus === 404 ? "gone" : "error"
  }
  return "loading"
}

export const errorStatusOf = (error: unknown): number | null =>
  isAxiosError(error) ? (error.response?.status ?? null) : null

/** A closed battle always closes the screen; only the follow-up window is once per id via `claim`. */
export const battleEndStep = (
  view: IBattleView,
  claim: (battleId: string) => boolean
): IBattleEndStep | null => {
  if (view.state === BATTLE_STATE.OPEN) {
    return null
  }
  const plan = planBattleEnd(view)
  return { followUp: plan && claim(view.id) ? plan : null }
}

const ROW_ORDER = Object.values(BATTLE_ROW)
const COLUMN_ORDER = Object.values(BATTLE_COLUMN)
const TEAM_A_COLUMNS: BATTLE_COLUMN[] = [BATTLE_COLUMN.C1, BATTLE_COLUMN.C2]

const MOVE_STEPS: Record<BATTLE_MOVE_DIRECTION, [number, number]> = {
  [BATTLE_MOVE_DIRECTION.UP]: [0, -1],
  [BATTLE_MOVE_DIRECTION.DOWN]: [0, 1],
  [BATTLE_MOVE_DIRECTION.LEFT]: [-1, 0],
  [BATTLE_MOVE_DIRECTION.RIGHT]: [1, 0]
}

export const deriveMoveTargets = (
  view: IBattleView,
  legal: IBattleLegalActions | undefined,
  canAct: boolean
): Record<string, BATTLE_MOVE_DIRECTION> => {
  const cell = heroOf(view)?.cell
  if (!canAct || !legal || !cell) {
    return {}
  }
  const targets: Record<string, BATTLE_MOVE_DIRECTION> = {}
  legal.moveDirections.forEach((direction) => {
    const [dColumn, dRow] = MOVE_STEPS[direction]
    const column = COLUMN_ORDER[COLUMN_ORDER.indexOf(cell.column) + dColumn]
    const row = ROW_ORDER[ROW_ORDER.indexOf(cell.row) + dRow]
    if (column !== undefined && row !== undefined) {
      targets[`${column}:${row}`] = direction
    }
  })
  return targets
}

export const deriveBattleField = (
  view: IBattleView,
  moveTargets: Record<string, BATTLE_MOVE_DIRECTION> = {}
): IBattleFieldCellView[] => {
  const heroTeam = heroOf(view)?.team ?? BATTLE_TEAM.A
  const sideOf = (column: BATTLE_COLUMN): IBattleSide => {
    const columnTeam = TEAM_A_COLUMNS.includes(column)
      ? BATTLE_TEAM.A
      : BATTLE_TEAM.B
    return columnTeam === heroTeam ? "ally" : "enemy"
  }

  return [...(view.grid ?? [])]
    .sort(
      (a, b) =>
        ROW_ORDER.indexOf(a.row) - ROW_ORDER.indexOf(b.row) ||
        COLUMN_ORDER.indexOf(a.column) - COLUMN_ORDER.indexOf(b.column)
    )
    .map(({ column, row, fighterId }) => {
      const side = sideOf(column)
      const fighter = view.fighters.find((f) => f.fighterId === fighterId)
      const token =
        fighter && fighter.alive && fighter.cell !== null
          ? {
              fighterId: fighter.fighterId,
              archetype: fighter.archetype,
              isHero: fighter.fighterId === view.heroFighterId,
              side
            }
          : null
      const key = `${column}:${row}`
      return { key, side, token, move: moveTargets[key] ?? null }
    })
}

export const fighterClassKeys = (
  fighter: IBattleFighter | undefined
): string[] => {
  if (!fighter) {
    return []
  }
  return [
    `archetype.${fighter.archetype}`,
    `weapon.${fighter.weaponType}`,
    ...(fighter.specialization === null
      ? []
      : [`specialization.${fighter.specialization}`])
  ]
}
